import React from 'react';
import { ArrowLeft, Sparkles, Package, Droplets } from 'lucide-react';

interface AboutUsProps {
  onBackToWaitlist: () => void;
}

export default function AboutUs({ onBackToWaitlist }: AboutUsProps) {
  return (
    <>
      <style jsx>{`
        .font-inter { font-family: 'Inter', sans-serif; }
        .font-poppins { font-family: 'Poppins', sans-serif; }
        
        .glass-morphism {
          background: rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(24px);
          border: 1px solid rgba(255, 255, 255, 0.12);
        }
        
        .premium-card {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
          backdrop-filter: blur(30px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }
        
        .gradient-text {
          background: linear-gradient(135deg, #ec4899, #8b5cf6, #06b6d4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .hover-lift:hover {
          transform: translateY(-8px);
        }
      `}</style>

      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 relative overflow-hidden">
        {/* Back Button */}
        <button
          onClick={onBackToWaitlist}
          className="fixed top-6 left-6 z-50 glass-morphism text-pink-400 px-6 py-3 rounded-full font-medium hover:bg-pink-500/10 transition-all duration-300 flex items-center space-x-2"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Back to Home</span>
        </button>

        {/* Header */}
        <section className="pt-32 pb-20 relative">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <div className="mb-12">
              <img 
                src="/logo transparent with tagline.png" 
                alt="UNDRESSING™ Sauce Logo" 
                className="h-24 w-auto mx-auto mb-8 object-contain"
              />
            </div>
            
            <h1 className="font-poppins font-black text-6xl lg:text-8xl text-white mb-8">
              About
              <span className="gradient-text block">UNDRESSING™ Sauce</span>
            </h1>
            
            <p className="font-inter text-2xl text-slate-300 max-w-4xl mx-auto leading-relaxed">
              UNDRESSING™ Sauce is the modern, on-the-go condiment crafted for beauty seekers, wellness warriors, athletes, creators, and everyday hustlers who crave bold taste, clean nutrition, and zero kitchen drama. Whether you're fueling a workout, elevating a desk lunch, or nourishing from the inside out—UNDRESSING™ Sauce is flavor without compromise.
            </p>
          </div>
        </section>

        {/* The Story */}
        <section className="py-20 relative">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="font-poppins font-black text-5xl lg:text-6xl text-white mb-8">
                  The Story Behind
                  <span className="gradient-text block">Strip • Drip • Glow™</span>
                </h2>
                <div className="space-y-6 font-inter text-xl text-slate-300 leading-relaxed">
                  <p>
                    UNDRESSING™ Sauce was born from a simple frustration: why should great flavor be limited to the kitchen? Our founders, a team of culinary innovators and wellness enthusiasts, saw the gap between convenience and quality in the condiment world.
                  </p>
                  <p>
                    Traditional sauce packets were wasteful, messy, and frankly, uninspiring. We knew there had to be a better way—one that honored both the art of flavor and the demands of modern life.
                  </p>
                  <p>
                    After years of research and development, we created the S-silhouette pouch: a revolutionary design that's as beautiful as it is functional. Each pouch delivers bold flavors while supporting your wellness journey, proving that innovation and intention can transform even the simplest things.
                  </p>
                </div>
              </div>

              <div className="relative">
                <div className="premium-card rounded-3xl p-8">
                  <img 
                    src="/photoshoot/undressing shoot 2.png"
                    alt="UNDRESSING™ Sauce Story"
                    className="w-full h-96 object-cover rounded-2xl"
                  />
                </div>
                
                {/* Floating Elements */}
                <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center">
                  <Droplets className="h-12 w-12 text-white" />
                </div>
                
                <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center">
                  <Package className="h-12 w-12 text-white" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-20 relative">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <div className="premium-card rounded-3xl p-16">
              <h2 className="font-poppins font-black text-5xl text-white mb-8">
                Join the Flavor
                <span className="gradient-text block">Revolution</span>
              </h2>
              <p className="font-inter text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
                Ready to transform your meals? Be part of the UNDRESSING™ Sauce community and experience flavor without compromise.
              </p>
              <button 
                onClick={onBackToWaitlist}
                className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-12 py-5 font-poppins font-bold text-xl hover:shadow-2xl hover:shadow-pink-500/25 transition-all duration-300 hover:scale-105 rounded-2xl"
              >
                Join the Waitlist
              </button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}